import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import {
    BandSource,
    EMPTY,
    IntegralPair,
    PairPoint,
    bandColumn,
    bandSeries,
    formatAmps,
    formatSeconds,
    makeBackgroundHook,
    makeHoverHook,
    makeRangeHook,
    pickTrace,
    useChartTheme,
} from './plotting';

const HEIGHT = 450;

// A zoom drag ends in a click on the same element
const CLICK_TOLERANCE_PX = 4;
// Below this a drag is a slipped click and must not zoom
const DRAG_DIST_PX = 10;
const DOUBLE_CLICK_MS = 250;
// How close the pointer must come to a chosen point to offer removing it
const HOVER_RADIUS_PX = 14;
const POINT_SIZE = 12;

const TOOLTIP_STYLE: Record<string, string> = {
    position: 'absolute',
    display: 'none',
    background: 'rgba(0,0,0,0.85)',
    color: '#fff',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'system-ui, sans-serif',
    pointerEvents: 'none',
    zIndex: '100',
    lineHeight: '1.5',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
};

export type SeriesKey = 'raw' | 'spline' | 'corrected' | 'picked';

// Picked points carry x/y values rather than indices, so a marker whose x has
// no exact match still lands somewhere sensible
const nearestIndex = (time: number[], x: number) => {
    let lo = 0;
    let hi = time.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (time[mid] < x) lo = mid + 1;
        else hi = mid;
    }
    if (lo > 0 && Math.abs(time[lo - 1] - x) <= Math.abs(time[lo] - x)) {
        return lo - 1;
    }
    return lo;
};

interface ChannelChartProps {
    title: string;
    time: number[];
    raw: number[];
    spline?: number[];
    corrected?: number[];
    picked?: PairPoint[];
    pickedLabel?: string;
    // Which signal a click reports a point from. Omit to disable picking.
    pickFrom?: 'raw' | 'corrected';
    pickingEnabled?: boolean;
    onPick?: (point: PairPoint) => void;
    bands?: IntegralPair[];
    // Called when a range end is clicked, with the range's position in bands
    onRemoveBand?: (index: number) => void;
    // Series that start toggled off in the legend
    hidden?: SeriesKey[];
}

export const ChannelChart = ({
    title,
    time,
    raw,
    spline,
    corrected,
    picked,
    pickedLabel = 'Selected points',
    pickFrom,
    pickingEnabled = true,
    onPick,
    bands,
    onRemoveBand,
    hidden,
}: ChannelChartProps) => {
    const theme = useChartTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<uPlot | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [width, setWidth] = useState(0);

    const hasSpline = (spline ?? []).length > 0;
    const hasCorrected = (corrected ?? []).length > 0;
    // Ranges draw their own brackets, so a plot showing only those needs no
    // marker series and no legend row for one
    const hasPicked = picked !== undefined;

    // The instrument repeats timestamps. The API resolves a chosen x to the
    // first of them, so this must agree.
    const indexByTime = useMemo(() => {
        const lookup = new Map<number, number>();
        time.forEach((value, index) => {
            if (!lookup.has(value)) lookup.set(value, index);
        });
        return lookup;
    }, [time]);

    const pickedValues = useMemo(() => {
        const values = new Array<number | null>(time.length).fill(null);
        (picked ?? []).forEach(point => {
            const index = indexByTime.get(point.x) ?? nearestIndex(time, point.x);
            if (index >= 0 && index < values.length) {
                values[index] = point.y;
            }
        });
        return values;
    }, [time, picked, indexByTime]);

    // Only completed ranges become series; an open one has nothing to fill
    const activeBands = useMemo(
        () => (bands ?? []).filter(band => band.end),
        [bands],
    );

    const bandColumns = useMemo(
        () => (hasCorrected
            ? activeBands.map(band => bandColumn(band, time, corrected!))
            : []),
        [activeBands, time, corrected, hasCorrected],
    );

    const aligned = useMemo(() => {
        const ys: (number | null)[][] = [raw];
        if (hasSpline) ys.push(spline!);
        if (hasCorrected) ys.push(corrected!);
        if (hasPicked) ys.push(pickedValues);
        ys.push(...bandColumns);
        return [time, ...ys] as uPlot.AlignedData;
    }, [
        time, raw, spline, corrected, pickedValues, bandColumns,
        hasSpline, hasCorrected, hasPicked,
    ]);

    // Read by the draw hook and the click listener, which outlive the render
    // that created them
    const sourceRef = useRef<BandSource>({ time, corrected: [], bands: [] });
    sourceRef.current = {
        time,
        corrected: corrected ?? [],
        bands: bands ?? [],
    };

    const pickRef = useRef<{
        time: number[];
        values: number[];
        enabled: boolean;
        onPick?: (point: PairPoint) => void;
    }>({ time, values: [], enabled: false });
    pickRef.current = {
        time,
        values: (pickFrom === 'corrected' ? corrected : raw) ?? [],
        enabled: pickingEnabled,
        onPick,
    };

    // What the pointer is over, which a click would remove: a chosen point, or
    // one end of a range
    const pickedRef = useRef<PairPoint[]>(EMPTY);
    pickedRef.current = picked ?? EMPTY;
    const hoverRef = useRef<PairPoint | null>(null);
    const hoverBandRef = useRef<number | null>(null);
    const removeBandRef = useRef<((index: number) => void) | undefined>(undefined);
    removeBandRef.current = onRemoveBand;

    const alignedRef = useRef(aligned);
    alignedRef.current = aligned;
    const appliedRef = useRef<uPlot.AlignedData | null>(null);
    const zoomRef = useRef<{ min: number; max: number } | null>(null);

    // Colours and the series list are baked into the options, so a change to
    // either rebuilds the chart. Range names are absent: renaming fires on
    // every keystroke and the legend text is patched in place instead.
    const shapeKey = [
        title,
        theme.isDark,
        hasSpline,
        hasCorrected,
        hasPicked,
        pickFrom ?? '',
        (hidden ?? []).join(','),
        activeBands.length,
    ].join('|');

    const canRender = width > 0;

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !canRender) return;

        // Captured before construction, which fires setScale
        const zoom = zoomRef.current;
        const isHidden = (key: SeriesKey) => (hidden ?? []).includes(key);
        const axis = (label: string, values?: uPlot.Axis['values']) => ({
            label,
            labelFont: '12px system-ui',
            font: '11px system-ui',
            stroke: theme.fontColor,
            grid: { stroke: theme.gridColor },
            ticks: { stroke: theme.gridColor },
            values,
        });

        // uPlot normalises series.stroke to a function on init
        const strokes: string[] = ['', hasCorrected ? theme.colors.rawMuted : theme.colors.raw];
        const series: uPlot.Series[] = [
            {},
            {
                label: 'Raw data',
                stroke: strokes[1],
                width: 1.5,
                show: !isHidden('raw'),
            },
        ];
        if (hasSpline) {
            strokes.push(theme.colors.spline);
            series.push({
                label: 'Baseline spline',
                stroke: theme.colors.spline,
                width: 1.5,
                dash: [4, 4],
                show: !isHidden('spline'),
            });
        }
        if (hasCorrected) {
            strokes.push(theme.colors.corrected);
            series.push({
                label: 'Corrected signal',
                stroke: theme.colors.corrected,
                width: 1.5,
                show: !isHidden('corrected'),
            });
        }
        if (hasPicked) {
            strokes.push(theme.colors.selected);
            series.push({
                label: pickedLabel,
                stroke: theme.colors.selected,
                // Markers only, with no connecting line between picked points
                paths: () => null,
                points: {
                    show: true,
                    size: POINT_SIZE,
                    fill: theme.colors.selected,
                    stroke: theme.colors.ring,
                    width: 1.5,
                },
                // Markers sit on the curve and must not drive the y axis
                auto: false,
                show: !isHidden('picked'),
            });
        }

        activeBands.forEach((band, index) => {
            strokes.push(theme.colors.fills[index % theme.colors.fills.length]);
            series.push(bandSeries(band, index, theme));
        });

        let downX = 0;
        let downY = 0;
        const baseCursor = () => (pickFrom && pickRef.current.enabled ? 'crosshair' : 'default');
        const opts: uPlot.Options = {
            width: container.clientWidth || width,
            height: HEIGHT,
            // Seconds from the start of the run, not epoch timestamps
            scales: { x: { time: false } },
            cursor: {
                drag: {
                    x: true,
                    y: false,
                    dist: DRAG_DIST_PX,
                    // uPlot's default swallows the click after any pointer
                    // movement at all, which takes slightly slipped picks with
                    // it; only a real drag's click is suppressed here
                    click: (_u: uPlot, event: MouseEvent) => {
                        const movedX = Math.abs(event.clientX - downX);
                        const movedY = Math.abs(event.clientY - downY);
                        if (movedX > CLICK_TOLERANCE_PX || movedY > CLICK_TOLERANCE_PX) {
                            pickTrace('dropped by uPlot: pointer moved, counted as a drag', {
                                movedX,
                                movedY,
                                allowedPx: CLICK_TOLERANCE_PX,
                            });
                            event.stopPropagation();
                            event.stopImmediatePropagation();
                        }
                    },
                },
            },
            legend: { show: true, live: false },
            series,
            axes: [
                axis('Time [s]'),
                {
                    ...axis('Current [A]', (_u, splits) => splits.map(formatAmps)),
                    size: 70,
                },
            ],
            hooks: {
                drawClear: [makeBackgroundHook(theme)],
                draw: [
                    makeRangeHook(() => sourceRef.current, theme),
                    makeHoverHook(() => hoverRef.current, theme),
                ],
                setScale: [(u: uPlot, key: string) => {
                    if (key !== 'x') return;
                    const { min, max } = u.scales.x;
                    const xs = u.data[0];
                    // Only a window narrower than the data counts as a zoom
                    const zoomed = min != null && max != null && xs.length > 0
                        && (min > (xs[0] as number) || max < (xs[xs.length - 1] as number));
                    zoomRef.current = zoomed ? { min: min!, max: max! } : null;
                    pickTrace('x range now', { min, max, keptAsZoom: zoomed ? 'yes' : 'no' });
                }],
                setCursor: [
                    (u: uPlot) => {
                        const tooltip = tooltipRef.current;
                        if (!tooltip || !containerRef.current) return;
                        const { left, top, idx } = u.cursor;
                        if (idx == null || left == null || top == null) {
                            tooltip.style.display = 'none';
                            if (hoverRef.current) {
                                hoverRef.current = null;
                                u.redraw(false);
                            }
                            return;
                        }

                        // A chosen point or a range end under the pointer is
                        // offered for removal, which a click carries out
                        const near = (point: PairPoint) => {
                            const dx = u.valToPos(point.x, 'x') - left;
                            const dy = u.valToPos(point.y, 'y') - top;
                            return dx * dx + dy * dy <= HOVER_RADIUS_PX * HOVER_RADIUS_PX;
                        };
                        let hovered: PairPoint | null = null;
                        let hoveredBand: number | null = null;
                        if (pickRef.current.enabled) {
                            hovered = pickedRef.current.find(near) ?? null;
                            if (!hovered && removeBandRef.current) {
                                sourceRef.current.bands.forEach((band, index) => {
                                    if (hoveredBand != null || !band.end) return;
                                    if (near(band.start) || near(band.end)) {
                                        hoveredBand = index;
                                        hovered = near(band.start) ? band.start : band.end!;
                                    }
                                });
                            }
                        }
                        if (hovered !== hoverRef.current || hoveredBand !== hoverBandRef.current) {
                            hoverRef.current = hovered;
                            hoverBandRef.current = hoveredBand;
                            u.over.style.cursor = hovered ? 'pointer' : baseCursor();
                            u.redraw(false);
                        }

                        const rows: string[] = [];
                        if (hovered) {
                            rows.push(
                                `<b>${formatSeconds(hovered.x)} s</b>`,
                                `${formatAmps(hovered.y)} A`,
                                `<span style="opacity:0.75">Click to remove this ${
                                    hoveredBand != null ? 'range' : 'point'}</span>`,
                            );
                        } else {
                            rows.push(`<b>${formatSeconds(u.data[0][idx] as number)} s</b>`);
                            for (let i = 1; i < u.series.length; i++) {
                                if (!u.series[i].show) continue;
                                const value = u.data[i][idx];
                                if (value == null) continue;
                                rows.push(
                                    `<span style="display:inline-block;width:12px;height:4px;background:${strokes[i]};margin:0 4px 2px;vertical-align:middle;border-radius:1px"></span>` +
                                    `${u.series[i].label}: ${formatAmps(value)}`
                                );
                            }
                        }
                        tooltip.innerHTML = rows.join('<br>');
                        tooltip.style.display = 'block';
                        const overRect = u.over.getBoundingClientRect();
                        const containerRect = containerRef.current.getBoundingClientRect();
                        const x = overRect.left - containerRect.left + left;
                        const y = overRect.top - containerRect.top + top;
                        const tooltipWidth = tooltip.offsetWidth;
                        tooltip.style.left =
                            (x + tooltipWidth + 20 > containerRect.width ? x - tooltipWidth - 10 : x + 15) + 'px';
                        tooltip.style.top = Math.max(y - tooltip.offsetHeight / 2, 0) + 'px';
                    },
                ],
            },
        };

        const chart = new uPlot(opts, alignedRef.current, container);
        chartRef.current = chart;
        appliedRef.current = alignedRef.current;
        // A new chart has no range until one is set, so the x scale is always
        // stated here: the zoom the user was on, or the full extent. Completing
        // a range adds a series, which rebuilds the chart mid-selection.
        const xs = alignedRef.current[0] as number[];
        const extent = xs.length
            ? { min: xs[0], max: xs[xs.length - 1] }
            : null;
        const range = zoom ?? extent;
        if (range) {
            chart.setScale('x', range);
        }
        pickTrace('chart built', {
            picking: pickFrom ?? 'off',
            series: series.length - 1,
            samples: time.length,
            range: range ? `${range.min}..${range.max}` : 'none',
            restored: zoom ? 'zoom' : 'full extent',
        });

        if (!pickFrom) {
            return () => {
                chart.destroy();
                chartRef.current = null;
                appliedRef.current = null;
            };
        }

        let pending: { timer: ReturnType<typeof setTimeout>; fire: () => void } | null = null;
        const handleDown = (event: MouseEvent) => {
            downX = event.clientX;
            downY = event.clientY;
        };
        const handleClick = (event: MouseEvent) => {
            // A zoom drag ends in a click here
            const movedX = Math.abs(event.clientX - downX);
            const movedY = Math.abs(event.clientY - downY);
            if (movedX > CLICK_TOLERANCE_PX || movedY > CLICK_TOLERANCE_PX) {
                pickTrace('ignored: pointer moved between press and release', {
                    movedX,
                    movedY,
                    allowedPx: CLICK_TOLERANCE_PX,
                });
                return;
            }
            // The first click of a double-click reports detail 1, so a pick
            // waits out the double-click window and a reset cancels it
            if (event.detail > 1) {
                pickTrace('ignored: second click of a double click', { detail: event.detail });
                if (pending) {
                    clearTimeout(pending.timer);
                    pending = null;
                }
                return;
            }
            const { time: xs, values, enabled, onPick: handler } = pickRef.current;
            if (!handler || !enabled) {
                pickTrace('ignored: picking is off, turn on Edit points', {
                    armed: enabled,
                    hasHandler: Boolean(handler),
                });
                return;
            }

            // A range end takes its own click, removing the whole range
            const band = hoverBandRef.current;
            if (band != null && removeBandRef.current) {
                pickTrace('removing the range under the pointer', { range: band });
                hoverRef.current = null;
                hoverBandRef.current = null;
                removeBandRef.current(band);
                return;
            }

            let point: PairPoint;
            const hovered = hoverRef.current;
            if (hovered) {
                // Removal reports the point's own position: where samples are
                // closer together than the hover radius, the sample nearest
                // the pointer is a different one and would add a point
                point = hovered;
                hoverRef.current = null;
                pickTrace('removing the point under the pointer', { time: point.x });
            } else {
                // The same sample the cursor and tooltip are already on. It is
                // found by x alone, so the click does not have to land on the
                // trace itself: anywhere in the column takes the reading the
                // tooltip is showing, and the value always comes from the
                // signal being picked from.
                const idx = chart.cursor.idx;
                if (idx == null) {
                    pickTrace('ignored: no sample under the pointer');
                    return;
                }
                const y = values[idx];
                if (y == null) {
                    pickTrace('ignored: this sample has no value on the picking signal', {
                        idx,
                        time: xs[idx],
                        signal: pickFrom,
                    });
                    return;
                }
                point = { x: xs[idx], y };
                pickTrace('accepted', { time: point.x, value: point.y, signal: pickFrom });
            }
            // A second pick elsewhere lands the first immediately
            if (pending) {
                clearTimeout(pending.timer);
                pending.fire();
            }
            const fire = () => {
                pending = null;
                pickTrace('sent to the form', { time: point.x });
                handler(point);
            };
            pending = { timer: setTimeout(fire, DOUBLE_CLICK_MS), fire };
        };
        const handleDblClick = () => {
            if (pending) {
                pickTrace('cancelled: double click, zoom reset instead');
                clearTimeout(pending.timer);
                pending = null;
            }
        };
        chart.over.addEventListener('mousedown', handleDown);
        chart.over.addEventListener('click', handleClick);
        chart.over.addEventListener('dblclick', handleDblClick);

        // The browser only raises a click when press and release agree on an
        // element, so a press that produces no click at all is worth seeing:
        // it means the plot moved or was rebuilt underneath the pointer.
        const describe = (target: EventTarget | null) => {
            if (!(target instanceof Element)) return 'nothing';
            const classes = String(target.className || '').trim().split(/\s+/).filter(Boolean);
            return target.tagName.toLowerCase() + (classes.length ? `.${classes.join('.')}` : '');
        };
        const traceRaw = (event: Event) => {
            if (!(event.target instanceof Node) || !container.contains(event.target)) return;
            pickTrace(`browser ${event.type}`, {
                on: describe(event.target),
                detail: (event as MouseEvent).detail,
            });
        };
        const rawTypes = ['mousedown', 'mouseup', 'click', 'dblclick'];
        for (const type of rawTypes) window.addEventListener(type, traceRaw, true);

        return () => {
            if (pending) {
                pickTrace('pick lost: the chart was rebuilt before it was sent');
                clearTimeout(pending.timer);
            }
            for (const type of rawTypes) window.removeEventListener(type, traceRaw, true);
            chart.over.removeEventListener('mousedown', handleDown);
            chart.over.removeEventListener('click', handleClick);
            chart.over.removeEventListener('dblclick', handleDblClick);
            pickTrace('chart torn down');
            chart.destroy();
            chartRef.current = null;
            appliedRef.current = null;
        };
    }, [shapeKey, canRender]); // eslint-disable-line react-hooks/exhaustive-deps

    // Signals whether a click will land a point
    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.over.style.cursor =
                pickFrom && pickingEnabled ? 'crosshair' : 'default';
        }
    }, [pickFrom, pickingEnabled, shapeKey, canRender]);

    // setData leaves the previous frame on screen unless the scales move, so
    // updates go through the default rescale and a zoom is put back after
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart || appliedRef.current === aligned) return;
        const zoom = zoomRef.current;
        chart.setData(aligned);
        if (zoom) chart.setScale('x', zoom);
        appliedRef.current = aligned;
    }, [aligned]);

    // Retitle the rendered legend row rather than rebuilding the chart
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const firstBand = chart.series.length - activeBands.length;
        const labels = chart.root.querySelectorAll<HTMLElement>('.u-legend .u-label');
        activeBands.forEach((band, index) => {
            const name = band.sample_name || `Sample ${index + 1}`;
            chart.series[firstBand + index].label = name;
            // The legend has no row for the x series
            const row = labels[firstBand + index - 1];
            if (row && row.textContent !== name) row.textContent = name;
        });
        // Repaint only: redraw() re-applies the current x scale, which on a
        // chart that has just been built is still unset
        chart.redraw(false);
    }, [bands, activeBands]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const observer = new ResizeObserver(() => setWidth(container.clientWidth));
        observer.observe(container);
        setWidth(container.clientWidth);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (chartRef.current && width > 0) {
            chartRef.current.setSize({ width, height: HEIGHT });
        }
    }, [width]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const tooltip = document.createElement('div');
        Object.assign(tooltip.style, TOOLTIP_STYLE);
        container.appendChild(tooltip);
        tooltipRef.current = tooltip;
        return () => {
            tooltip.remove();
            tooltipRef.current = null;
        };
    }, []);

    return (
        <Box
            sx={{
                width: '100%',
                minWidth: 0,
                '& .u-select': { background: theme.selectColor },
            }}
        >
            <Typography variant="subtitle1" align="center">
                {title}
            </Typography>
            {/* react-admin's layout root is min-width: fit-content, so uPlot's
                pixel width would set the page minimum and nothing could shrink.
                Containment keeps the width a consequence of the page. */}
            <div
                ref={containerRef}
                style={{
                    position: 'relative',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    contain: 'inline-size',
                }}
            />
        </Box>
    );
};

export default ChannelChart;
